import * as HoverCardPrimitive from '@radix-ui/react-hover-card';
import * as React from 'react';

import { cn } from '@heidi/utils';

import type {
  CitationPopoverContentProps,
  CitationPopoverProps,
} from './citation-popover.types';

/**
 * CitationPopover - A hover card specifically designed for source citations
 *
 * Uses Portal to render outside scroll containers, ensuring proper alignment
 * when used inside ScrollArea components like the chat timeline.
 *
 * closeDelay: 400ms allows time to move mouse from badge to popover content
 */
const CitationPopover = ({ closeDelay = 400, ...props }: CitationPopoverProps) => (
  <HoverCardPrimitive.Root closeDelay={closeDelay} {...props} />
);

const CitationPopoverTrigger = HoverCardPrimitive.Trigger;

const CitationPopoverContent = React.forwardRef<
  React.ElementRef<typeof HoverCardPrimitive.Content>,
  CitationPopoverContentProps
>(({ className, align = 'start', sideOffset = 4, ...props }, ref) => (
  <HoverCardPrimitive.Portal>
    <HoverCardPrimitive.Content
      ref={ref}
      align={align}
      sideOffset={sideOffset}
      data-testid="citation-popover-content"
      className={cn(
        // Figma: rounded-lg (12px), shadow-xl
        // w-96 = 384px, max-w-md = 448px - wider to accommodate rendered markdown snippets
        'z-999 w-[28rem] overflow-hidden rounded-lg border border-border bg-surface p-0 text-text-primary shadow-xl outline-none',
        'data-[state=open]:animate-in data-[state=closed]:animate-out',
        'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
        'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
        'data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2',
        'data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
        className
      )}
      {...props}
    />
  </HoverCardPrimitive.Portal>
));
CitationPopoverContent.displayName = 'CitationPopoverContent';

export { CitationPopover, CitationPopoverContent, CitationPopoverTrigger };
